import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Button } from 'components/Button';
import { Modal, ModalFooter, ModalTitle } from 'components/Modal';
import { Label, Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { IRemainderBeneficiary, ITipCalculatorResultWarning, ITipCalculatorResultWarningCappedRemainder, ITipCalculatorResultWarningCappedShare, ITipCalculatorResultWarningCappedShareDetailRule, TipCalculatorWarningType } from 'types';
import { centsToDollarsDisplay, sortAlphaNumerically, toTitleCase } from 'utils';
import { renderRemainderBeneficiaryDescription, renderShareRuleDescription } from '../TipShareRules/TipShareRules';

const SectionWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin: ${toRem(theme.space.sm)} 0;
  font-family: ${theme.font.family.euclid};

  p.subtitle {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    margin: ${toRem(theme.space.sm)} 0;
    text-align: center;
  }
`;

const CappedItem = styled.div`
  display: flex;
  padding: ${toRem(theme.space.md)};
  flex-direction: column;
  align-items: flex-start;
  gap: ${toRem(theme.space.lg)};
  align-self: stretch;
  border-radius: ${toRem(8)};
  background: ${theme.color.neutral[50]};
  margin: ${toRem(theme.space.sm)} 0;
`;

const CapItemTitle = styled(Text)`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};
`;

const CapItemSubTitle = styled.span`
  color: ${theme.color.neutral[500]};
  font-weight: ${theme.font.weight.regular} !important;
`;

const CapItemDetails = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const CapItemRule = styled(Text)`
  color: ${theme.color.neutral[500]};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
  margin: 0;
`;

const CapItemTotals = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${toRem(theme.space.sm)};
  justify-content: space-between;
  width: 100%;
  margin-top: ${toRem(theme.space.md)};
`;

const CapTotalItem = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column nowrap;

  label,
  p {
    color: ${theme.color.neutral[500]};
    font-weight: ${theme.font.weight.regular};
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    margin-bottom: ${toRem(theme.space.xs)};
  }

  @media (max-width: 485px) {
    label {
      min-height: ${toRem(28)};
    }
  }
`;

interface ICapsModalProps {
  warnings: ITipCalculatorResultWarning[];
  dataCy?: string;
  hideModal?: () => void;
}

const CapsModal: React.FC<ICapsModalProps> = ({ warnings, hideModal, dataCy = 'cap-modal' }) => {
  const renderTipSharingCaps = useMemo(() => {
    const cappedShare = warnings.filter((warning) => warning.type === TipCalculatorWarningType.CAPPED_SHARE) as ITipCalculatorResultWarningCappedShare[];
    if (!cappedShare?.length) return <></>;

    const buildRule = (rule: ITipCalculatorResultWarningCappedShareDetailRule) => (
      <CapItemRule>
        <span>Rule: </span>
        {renderShareRuleDescription(rule)}
      </CapItemRule>
    );

    return (
      <SectionWrapper data-cy="tip-sharing-caps">
        <ModalTitle>Tip Sharing Caps</ModalTitle>
        <Text className="subtitle">The following tip shares have been capped because the employee did not collect enough Tips or Autograts to satisfy the rule.</Text>
        {cappedShare.sort(sortAlphaNumerically('employee.name')).map((item, itemIdx) => (
          <CappedItem key={`tip_share_cap_${itemIdx}`} data-cy="cap-item">
            <CapItemTitle>
              {item.employee.name},{' '}
              <CapItemSubTitle>
                {toTitleCase(item.contributor.name)}, {item.segment}
              </CapItemSubTitle>
            </CapItemTitle>
            {item.details.map((detail, detailIdx) => (
              <CapItemDetails key={`tip_share_cap_${itemIdx}_detail_${detailIdx}`} data-cy="cap-item-details">
                {buildRule(detail.rule)}
                <CapItemTotals data-cy="cap-item-details-totals">
                  <CapTotalItem>
                    <Label>Calculated Amount</Label>
                    <Text>{centsToDollarsDisplay(detail.funds.allocated)}</Text>
                  </CapTotalItem>
                  <CapTotalItem>
                    <Label>Capped Amount</Label>
                    <Text>{centsToDollarsDisplay(detail.funds.disbursed)}</Text>
                  </CapTotalItem>
                  <CapTotalItem>
                    <Label>Difference</Label>
                    <Text>{centsToDollarsDisplay(detail.funds.diff)}</Text>
                  </CapTotalItem>
                </CapItemTotals>
              </CapItemDetails>
            ))}
          </CappedItem>
        ))}
      </SectionWrapper>
    );
  }, [warnings]);

  const renderRemainderCaps = useMemo(() => {
    const cappedRemainder = warnings.filter((warning) => warning.type === TipCalculatorWarningType.CAPPED_REMAINDER) as ITipCalculatorResultWarningCappedRemainder[];
    if (!cappedRemainder?.length) return <></>;

    const buildRule = (beneficiaries: IRemainderBeneficiary[]) =>
      beneficiaries.map((beneficiary, idx) => (
        <CapItemRule key={`remaind-caps-rule-${idx}`}>
          <span>Rule: </span>
          {renderRemainderBeneficiaryDescription(beneficiary)}
        </CapItemRule>
      ));

    return (
      <SectionWrapper data-cy="remainder-caps">
        <ModalTitle>Remainder Pool Caps</ModalTitle>
        <Text className="subtitle">The following remainder rules have been capped because the remainder pool does not contain enough Tips or Autograts to satisfy the rule.</Text>
        {cappedRemainder.sort(sortAlphaNumerically('contributor.name')).map((item, itemIdx) => (
          <CappedItem key={`remainder_pool_caps_${itemIdx}`} data-cy="cap-item">
            <CapItemTitle>
              {toTitleCase(item.contributor.name)}, <CapItemSubTitle>{item.segment}</CapItemSubTitle>
            </CapItemTitle>
            {item.details.map((detail, detailIdx) => (
              <CapItemDetails key={`remainder_pool_caps_${itemIdx}_detail_${detailIdx}`} data-cy="cap-item-details">
                {buildRule(detail.rule.beneficiaries)}
                <CapItemTotals data-cy="cap-item-details-totals">
                  <CapTotalItem>
                    <Label>Calculated Amount</Label>
                    <Text>{centsToDollarsDisplay(detail.funds.allocated)}</Text>
                  </CapTotalItem>
                  <CapTotalItem>
                    <Label>Capped Amount</Label>
                    <Text>{centsToDollarsDisplay(detail.funds.disbursed)}</Text>
                  </CapTotalItem>
                  <CapTotalItem>
                    <Label>Difference</Label>
                    <Text>{centsToDollarsDisplay(detail.funds.diff)}</Text>
                  </CapTotalItem>
                </CapItemTotals>
              </CapItemDetails>
            ))}
          </CappedItem>
        ))}
      </SectionWrapper>
    );
  }, [warnings]);

  return (
    <Modal maxWidth={540} isActive onClose={hideModal} data-cy={dataCy} hideCloseButton>
      {renderTipSharingCaps}
      {renderRemainderCaps}
      <ModalFooter>
        <Button label="Dismiss" appearance="primary" onClick={hideModal} data-cy="dismiss-button" />
      </ModalFooter>
    </Modal>
  );
};

export default CapsModal;
